<template>
  <b-modal
    id="cancel-reservation-modal"
    v-model="visible"
    centered
    hide-footer
    title="Cancel reservation"
    modal-class="ds-modal"
    @hidden="resetForm"
  >
    <div class="dc-body">
      <!-- What is about to be cancelled, spelled out: the modal used to show only a
           date, which is thin reassurance for an action that can't be undone. -->
      <dl class="ds-modal-facts">
        <div
          v-for="fact in facts"
          :key="fact.label"
        >
          <dt>{{ fact.label }}</dt>
          <dd>{{ fact.value }}</dd>
        </div>
      </dl>

      <ds-note
        v-if="isSameDay"
        variant="warn"
      >
        <strong>This reservation is today.</strong> Last-minute cancellations are
        disappointing for the daters and may affect your partner rating. If there's anything
        we can help with, reach out before cancelling.
      </ds-note>

      <div class="dc-fields">
        <!-- Through DsField's scoped slot, not its default one: the field offers
             describedBy, the control has to bind it, or a screen reader user who tabs back
             to the control after the error is announced hears no association. -->
        <ds-field
          label="Why do you need to cancel?"
          label-for="cancel-reason"
          :error="reasonError"
          stacked
        >
          <template #default="{ describedBy, invalid }">
            <ds-select
              id="cancel-reason"
              v-model="selectedReason"
              :options="reasonOptions"
              :invalid="invalid"
              :aria-describedby="describedBy"
            />
          </template>
        </ds-field>

        <ds-field
          :label="notesLabel"
          label-for="cancel-notes"
          :hint="notesHint"
          :error="notesError"
          stacked
        >
          <template #default="{ describedBy, invalid }">
            <ds-textarea
              id="cancel-notes"
              v-model="additionalNotes"
              :placeholder="notesPlaceholder"
              :invalid="invalid"
              :aria-describedby="describedBy"
              rows="3"
            />
          </template>
        </ds-field>
      </div>

      <ds-note>
        <strong>Cancelling more than one?</strong> Add a block period under
        <a
          href="/blocked-days"
          target="_blank"
          rel="noopener"
        >Blocked days</a>
        to stop new bookings across a range of dates or hours.
      </ds-note>

      <div class="dc-actions">
        <ds-button
          variant="secondary"
          @click="visible = false"
        >
          Keep reservation
        </ds-button>
        <ds-button
          variant="danger"
          :disabled="isSubmitting"
          @click="submitCancellation"
        >
          {{ isSubmitting ? 'Cancelling…' : 'Cancel reservation' }}
        </ds-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { BModal } from 'bootstrap-vue'
import moment from 'moment'
import {
  DsButton, DsField, DsNote, DsSelect, DsTextarea,
} from '@/design-system/components'
import reservationFacts from '@/_helpers/reservation'

export default {
  name: 'DateDecline',
  components: {
    BModal,
    DsButton,
    DsField,
    DsNote,
    DsSelect,
    DsTextarea,
  },
  props: {
    show: Boolean,
    date: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      visible: this.show,
      selectedReason: null,
      additionalNotes: '',
      isSubmitting: false,
      validated: false,
      reasonOptions: [
        { value: null, text: 'Select a reason…', disabled: true },
        { value: 'fully_booked', text: 'Venue is fully booked' },
        { value: 'private_event', text: 'Private event' },
        { value: 'venue_closed', text: 'Venue closed' },
        { value: 'staff_shortage', text: 'Staff shortage' },
        { value: 'maintenance', text: 'Maintenance / renovations' },
        { value: 'emergency', text: 'Emergency situation' },
        { value: 'other', text: 'Other reason' },
      ],
    }
  },
  computed: {
    facts() {
      return reservationFacts(this.date)
    },
    isSameDay() {
      if (!this.date?.start) return false
      return moment(this.date.start).isSame(moment(), 'day')
    },
    isNotesRequired() {
      return this.selectedReason === 'other'
    },
    notesLabel() {
      return this.isNotesRequired ? 'Tell us more' : 'Additional notes'
    },
    notesHint() {
      return this.isNotesRequired ? 'Required for "Other reason".' : 'Optional. Shared with our team, not the daters.'
    },
    notesPlaceholder() {
      return this.isNotesRequired
        ? 'Why do you need to cancel?'
        : 'Anything you\'d like us to know…'
    },
    // Errors surface through DsField, which owns the aria-describedby wiring.
    reasonError() {
      if (!this.validated || this.selectedReason !== null) return ''
      return 'Select a reason for cancelling.'
    },
    notesError() {
      if (!this.validated || !this.isNotesRequired || this.additionalNotes.trim()) return ''
      return 'Add some detail about the cancellation.'
    },
  },
  watch: {
    show() {
      this.visible = this.show
    },
    visible() {
      this.$emit('update:show', this.visible)
    },
  },
  methods: {
    resetForm() {
      this.selectedReason = null
      this.additionalNotes = ''
      this.validated = false
      this.isSubmitting = false
    },
    submitCancellation() {
      this.validated = true
      if (this.reasonError || this.notesError) return

      this.isSubmitting = true

      const reasonText = this.reasonOptions.find(o => o.value === this.selectedReason)?.text || ''
      const details = this.additionalNotes.trim()
        ? `${reasonText}: ${this.additionalNotes.trim()}`
        : reasonText

      this.$emit('cancel', { reason: this.selectedReason, details })
      this.visible = false
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/design-system/mixins';

// The shell (radius, header, close button) and .ds-modal-facts come from the
// `ds-modal` class, in assets/scss/style.scss.
.dc-body {
  @include ds-stack(16px);

  align-items: stretch;
}

// Tighter than a card's field list: two stacked fields, no dividers between them.
.dc-fields ::v-deep .ds-field {
  padding: 0;
  border-top: none;

  & + .ds-field {
    margin-top: 14px;
  }
}

.dc-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 4px;
}
</style>
